import { ChangeEvent, useState, useEffect } from "react";

import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { useYupErrors, useYupSchemas } from "../../../../../../hooks/useYupValidation";
import { DespatchTruck } from "../../../../../../models/despatch";
import { ValueOf } from "../../../../../../types";
import { DespatchesTruckFields } from "../../../../../../util/enums";

type Key = keyof DespatchTruck;

export const useTruckMaintenance = () => {
  const { t: translate } = useTranslation();

  const [fields, setFields] = useState<DespatchTruck>({} as DespatchTruck);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const onPopulateFields = (data: typeof fields) => {
    setFields(data);
  };

  const onResetFields = () => {
    setFields({} as DespatchTruck);
    setHasFormBeenSubmitted(false);
    setErrors([]);
  };

  const onUpdateField = (key: Key, value: ValueOf<DespatchTruck>) => {
    setFields((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onUpdateFieldEvent = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined,
    valueKey?: { value: ValueOf<DespatchTruck>; key: keyof DespatchTruck }
  ) => {
    if (valueKey && valueKey.key) {
      onUpdateField(valueKey.key, valueKey.value);

      return;
    }
    if (!event || !event.target.name) return;
    onUpdateField(event.target.name as keyof DespatchTruck, event.target.value);
  };

  const { fieldHasError, getErrorByField } = useYupErrors<DespatchTruck>(errors);
  const { stringRequired, numberRequired } = useYupSchemas(translate);

  const schema = yup.object().shape({
    [DespatchesTruckFields.FLEET_TYPE]: stringRequired,
    [DespatchesTruckFields.PALLETS_PER_FLEET]: numberRequired,
    [DespatchesTruckFields.DELIVERY_DATE]: stringRequired,
    [DespatchesTruckFields.DELIVERY_WINDOW_OPEN]: stringRequired,
    [DespatchesTruckFields.DELIVERY_WINDOW_CLOSE]: stringRequired,
  });

  const validateFields = (data: typeof fields) => {
    try {
      if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);
      schema.validateSync(data, { abortEarly: false });
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
  };

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      validateFields(fields);
    }
  }, [hasFormBeenSubmitted, fields]);

  return {
    fields,
    onUpdateFieldEvent,
    onPopulateFields,
    onResetFields,
    hasFormBeenSubmitted,
    fieldHasError,
    getErrorByField,
    validateFields,
  };
};
